import { STORAGE_KEYS } from './constants';

export interface PersonalFilterEntry
{
  dashboardId: number;
  request: Record<string, any>;
  isFirstLoad: boolean;
}

function load(): PersonalFilterEntry[]
{
  try
  {
    const raw = localStorage.getItem(STORAGE_KEYS.PERSONAL_FILTER_CONFIG);
    if (raw)
    {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  }
  catch { /* ignore */ }
  return [];
}

function save(config: PersonalFilterEntry[]): void
{
  try
  {
    localStorage.setItem(STORAGE_KEYS.PERSONAL_FILTER_CONFIG, JSON.stringify(config));
  }
  catch (error)
  {
    if (error instanceof DOMException && error.name === 'QuotaExceededError' && config.length > 1)
    {
      // Drop the oldest entries and retry once instead of crashing the app.
      save(config.slice(1));
      return;
    }
    console.error('Failed to save personalFilterConfig:', error);
  }
}

/** Returns true when the dashboard is stored as personal (Apply For All = OFF). */
export function isDashboardPersonal(dashboardId: number): boolean
{
  return load().some(e => e.dashboardId === dashboardId);
}

/**
 * Marks a dashboard as personal (isPersonal=true) or removes it from the list
 * (isPersonal=false, meaning Apply For All is ON for that dashboard).
 */
export function setDashboardPersonal(dashboardId: number, isPersonal: boolean): void
{
  const config = load();
  const idx = config.findIndex(e => e.dashboardId === dashboardId);

  if (isPersonal)
  {
    if (idx === -1)
    {
      config.push({ dashboardId, request: {}, isFirstLoad: true });
    }
  }
  else
  {
    if (idx !== -1)
    {
      config.splice(idx, 1);
    }
  }

  save(config);
}

/** Returns the stored entry for a dashboard, or undefined if not present. */
export function getPersonalFilterEntry(dashboardId: number): PersonalFilterEntry | undefined
{
  return load().find(e => e.dashboardId === dashboardId);
}

/** Updates the request snapshot for an existing personal-filter entry. */
export function updatePersonalFilterRequest(
  dashboardId: number,
  request: Record<string, any>,
): void
{
  const config = load();
  const entry = config.find(e => e.dashboardId === dashboardId);
  if (entry)
  {
    entry.request = request;
    entry.isFirstLoad = false;
    save(config);
  }
}
